import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationMeta } from '@/types/pagination';
import { cn } from '@/lib/utils';
import {
    listControlsGlassNavButton,
    listControlsGlassPaginationBar,
} from '@/components/capability-list/list-controls-classes';

export interface ListPaginationFooterProps {
    meta: PaginationMeta;
    onPageChange: (page: number) => void;
}

/**
 * Shared list footer: always shows result counts when total > 0;
 * shows prev / page numbers / next when there is more than one page.
 */
export function ListPaginationFooter({
    meta,
    onPageChange,
}: ListPaginationFooterProps) {
    if (meta.total <= 0) return null;

    const from =
        meta.from ??
        (meta.total > 0 ? (meta.current_page - 1) * meta.per_page + 1 : 0);
    const to =
        meta.to ??
        (meta.total > 0
            ? Math.min(meta.current_page * meta.per_page, meta.total)
            : 0);
    const hasMultiplePages = meta.last_page > 1;

    return (
        <div className={listControlsGlassPaginationBar}>
            <div className="flex items-center justify-between">
                <div className="text-sm text-[hsl(var(--list-controls-muted-foreground))]">
                    Showing{' '}
                    <span className="font-medium text-[hsl(var(--list-controls-foreground))]">
                        {from}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium text-[hsl(var(--list-controls-foreground))]">
                        {to}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium text-[hsl(var(--list-controls-foreground))]">
                        {meta.total}
                    </span>{' '}
                    results
                </div>

                {hasMultiplePages && (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onPageChange(meta.current_page - 1)}
                            disabled={meta.current_page <= 1}
                            className={listControlsGlassNavButton}
                        >
                            <ChevronLeft size={14} />
                            <span className="hidden sm:inline">Previous</span>
                        </button>

                        <div className="flex items-center gap-1">
                            {Array.from(
                                { length: meta.last_page },
                                (_, i) => i + 1,
                            ).map((page) => (
                                <button
                                    type="button"
                                    key={page}
                                    onClick={() => onPageChange(page)}
                                    className={cn(
                                        'min-w-[36px] rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                                        page === meta.current_page
                                            ? 'bg-[hsl(var(--list-controls-tab-active-bg))] text-[hsl(var(--list-controls-tab-active-fg))] shadow-sm'
                                            : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))]',
                                    )}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>

                        <button
                            type="button"
                            onClick={() => onPageChange(meta.current_page + 1)}
                            disabled={meta.current_page >= meta.last_page}
                            className={listControlsGlassNavButton}
                        >
                            <span className="hidden sm:inline">Next</span>
                            <ChevronRight size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
